import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CarFront, RefreshCw, ArrowUpRight, ArrowDownLeft, Clock } from "lucide-react";

const tm = (s) => new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
const ago = (s) => { const m = Math.round((Date.now() - new Date(s)) / 60000); return m < 60 ? `${m} min` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`; };

const GROUPS = [
  ["test_drive", "Test drives", "bg-[#BB162B]"],
  ["utility", "Utility runs", "bg-amber-600"],
  ["service", "Service centre / workshop", "bg-slate-600"],
  ["transfer", "Inter-branch transfers", "bg-indigo-600"],
];
const TD = { home: "Home TD", showroom: "Showroom TD", connected: "Connected drive", utility: "Utility", transfer: "Transfer", transfer_return: "Return of loaned car" };

function Row({ r }) {
  const dir = r.direction === "incoming" ? ["Incoming", "bg-emerald-100 text-emerald-700", ArrowDownLeft]
    : r.direction === "in_transit" ? ["In transit", "bg-indigo-100 text-indigo-700", ArrowUpRight] : null;
  const Icon = dir?.[2];
  return (
    <div data-testid={`out-now-row-${r.reg_no}`} className={`rounded-xl border bg-white p-3 ${r.overdue ? "border-red-300" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2"><b className="text-[#0E2240]">{r.reg_no}</b><span className="text-xs text-slate-500">{r.model}</span>
            {dir && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${dir[1]}`}><Icon size={10} /> {dir[0]}</span>}
            {r.overdue && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">Overdue</span>}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-600">{TD[r.td_type] || r.td_type}{r.purpose ? ` · ${r.purpose.replace("_", " ")}` : ""} · <b>{(r.drivers || []).filter(Boolean).join(" · ")}</b></div>
          <div className="truncate text-[11px] text-slate-500">
            {r.kind === "transfer" ? `${r.from_branch_name} → ${r.to_branch_name}` : r.destination ? `→ ${r.destination}` : r.customer_name}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-xs font-bold text-slate-700">OUT {tm(r.out_ts)}</div>
          <div className="flex items-center justify-end gap-1 text-[10px] text-slate-400"><Clock size={10} /> {ago(r.out_ts)}</div>
          {r.expected_back && <div className={`text-[10px] ${r.overdue ? "font-bold text-red-600" : "text-slate-400"}`}>due {tm(r.expected_back)}</div>}
        </div>
      </div>
    </div>
  );
}

export default function SecurityOutNow() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [data, setData] = useState(null);
  const [ts, setTs] = useState(null);
  const load = () => api.get("/gate/out-now", { params: branch ? { branch_id: branch } : {} }).then((r) => { setData(r.data); setTs(new Date()); }).catch(() => setData({ rows: [], count: 0 }));
  useEffect(() => { if (isAdmin) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [branch]);
  const rows = data?.rows || [];
  return (
    <div className="px-4 py-6 max-w-md mx-auto lg:max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0E2240] flex items-center gap-2"><CarFront size={22} /> Vehicles Out Now</h1>
          <p className="text-xs text-slate-500">{data?.branch_name || user.branch_name || "Your gate"} · every car outside the premises right now</p>
        </div>
        <button data-testid="out-now-refresh" onClick={load} className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500"><RefreshCw size={16} /></button>
      </div>
      {isAdmin && (
        <select data-testid="out-now-branch-filter" value={branch} onChange={(e) => setBranch(e.target.value)} className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
          <option value="">All locations</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}
      <div data-testid="out-now-count" className="mt-4 rounded-2xl bg-[#0E2240] p-4 text-white">
        <div className="font-heading text-4xl font-bold">{data ? data.count : "…"}</div>
        <div className="text-xs text-slate-300">vehicle{data?.count === 1 ? "" : "s"} not yet gated in{ts && ` · updated ${tm(ts)}`}</div>
      </div>
      {data && rows.length === 0 && <div className="mt-4 rounded-xl border border-dashed border-emerald-300 bg-emerald-50 p-6 text-center text-sm font-semibold text-emerald-700">All vehicles are inside — nothing pending</div>}
      {GROUPS.map(([k, label, dot]) => {
        const g = rows.filter((r) => r.kind === k);
        if (!g.length) return null;
        return (
          <div key={k} data-testid={`out-now-group-${k}`} className="mt-5">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500"><span className={`h-2 w-2 rounded-full ${dot}`} /> {label} <span className="rounded-full bg-slate-100 px-2 text-[10px] text-slate-600">{g.length}</span></div>
            <div className="mt-2 space-y-2">{g.map((r, i) => <Row key={r.reg_no + i} r={r} />)}</div>
          </div>
        );
      })}
    </div>
  );
}
